import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { ABOUT } from "@/constants/testIds/site";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBmaW5hbmNlJTIwdGVhbSUyMG1lZXRpbmd8ZW58MHx8fHwxNzgyOTQyNTQ2fDA&ixlib=rb-4.1.0&q=85";

export default function About() {
  const { t } = useLanguage();

  return (
    <section
      id="about"
      data-testid={ABOUT.section}
      className="py-20 md:py-32 border-t border-[#1A1A1A]/10 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[3/4] overflow-hidden">
              <img
                src={ABOUT_IMG}
                alt="Equipa de contabilistas em reunião"
                className="absolute inset-0 w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700 hover:scale-105"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-6 -right-4 md:-right-8 bg-[#8B1A1A] text-white px-6 py-4 max-w-[180px]">
              <div className="font-serif text-3xl leading-none">1998</div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.2em] opacity-80">
                Est.
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-7">
            <p className="font-sans uppercase tracking-[0.28em] text-xs text-[#8B1A1A] mb-6">
              {t.about.eyebrow}
            </p>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="font-serif font-light text-[#1A1A1A] text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            >
              {t.about.title}
            </motion.h2>

            <div className="mt-8 space-y-5 text-[#5A5A5A] text-base md:text-lg leading-relaxed max-w-2xl">
              <p>{t.about.p1}</p>
              <p>{t.about.p2}</p>
            </div>

            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
              {t.about.points.map((p, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="flex items-start gap-3 text-sm md:text-base text-[#1A1A1A]"
                >
                  <span className="mt-1 h-5 w-5 flex items-center justify-center border border-[#8B1A1A]/40 text-[#8B1A1A] shrink-0">
                    <Check className="h-3 w-3" strokeWidth={2.5} />
                  </span>
                  <span>{p}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
